import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { useContextSelector } from 'use-context-selector';
import Button from '@components/atoms/button/button';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import SellerFeesModal from '@components/seller-fees/seller-fees-modal/seller-fees-modal';
import BuyerFeesModal from '@components/buyer-fees/buyer-fees-modale/buyer-fees-modale';
import { PriceDropContext } from '@context/price-drop-context';
import { usePriceDropFormPresenter } from '@components/sell/catalog/products/list/list-item/item-cta/drop-price/form/price-drop-form-presenter';
import { RecommendationsPriceResponse } from '@interfaces/models/sales/domain/SellerPrice';
import { PriceDropBannerBox } from '../banner/PriceDropBannerBox';
import styles from './price-drop-form.module.scss';

export interface ProductFormProps {
  selectedProduct: RecommendationsPriceResponse | null;
}

export const PriceDropForm: React.FC<ProductFormProps> = ({ selectedProduct }) => {
  const { t: t1 } = useTranslation('price-drop');
  const { t: t2 } = useTranslation('common');
  const { t: tA11y } = useTranslation('a11y');
  const sellerPrice = useContextSelector(PriceDropContext, (a) => a.sellerPrice);
  const errorMessage = useContextSelector(PriceDropContext, (a) => a.error);
  const isPriceDetailsLoading = useContextSelector(PriceDropContext, (a) => a.isPriceDetailsLoading);
  const { sendAnalyticEvent, updateDataLayer } = useAnalyticEvents('my_listings');

  const {
    onSubmit,
    onChangePrice,
    onKeyDown,
    productPrice,
    buyerFeesPrice,
    sellerFees,
    sellerEarning,
    isBuyerFeesVisible,
    isSubmitButtonDisabled,
    priceInputClassNames,
    debouncedSellerPrice,
  } = usePriceDropFormPresenter('C2C');

  useEffect(() => {
    updateDataLayer({
      screen_category: 'my_listings',
      screen_subcategory: 'items_for_sale',
      screen_name: '/items_for_sale/drop_price',
      product_id: selectedProduct.id,
      product_brand: selectedProduct.brand.name,
      product_unit_price: String(selectedProduct.pricingBreakdown.sellerPrice.cents / 100),
      product_currency: selectedProduct.originalPrice.currency,
    });
  }, []);

  const onClick = () => {
    sendAnalyticEvent('mifs_price_drop_confirm', { value: String(debouncedSellerPrice) });
  };

  return (
    <form
      id="priceDropForm"
      onSubmit={onSubmit}
      className={styles.dropPrice__form}
    >
      <div className={styles.dropPrice__form__details}>
        <div className={styles.dropPrice__form__details__inputs}>
          <div
            className={clsx(styles.dropPrice__form__details__input__wrapper, {
              [styles['dropPrice__form__details__input__wrapper--error']]: errorMessage,
            })}
          >
            <label
              className={styles.dropPrice__form__details__inputs__label}
              htmlFor="sellerPrice"
            >
              {t2('BUYERFEE.INFOMODAL.PRICE')}
            </label>
            <div className={clsx(styles.dropPrice__form__details__inputs__wrapperWithCurrency, priceInputClassNames)}>
              <span
                className={styles.dropPrice__form__details__inputs__currencySymbol}
                data-cy="price-drop-currency-symbol"
              >
                {selectedProduct.currencySymbol}
              </span>
              <input
                className={styles.dropPrice__form__details__inputs__field}
                type="number"
                pattern="[0-9]*"
                min={0}
                max={productPrice}
                step="1"
                required
                onChange={onChangePrice}
                onKeyDown={onKeyDown}
                autoComplete="off"
                value={sellerPrice > 0 ? sellerPrice : ''}
                id="sellerPrice"
                name="sellerPrice"
              />
            </div>
          </div>
          <div
            className={clsx(
              styles.dropPrice__form__details__input__wrapper,
              styles['dropPrice__form__details__input__wrapper--output'],
            )}
          >
            <label
              className={clsx(
                styles.dropPrice__form__details__inputs__label,
                styles['dropPrice__form__details__inputs__label--output'],
              )}
              htmlFor="sellerEearnings"
            >
              {t1('PRICE_DROP.PRICE_INFO.YOU_EARN')}
              <SellerFeesModal feeDetails={sellerFees}>
                <Icon
                  name="info"
                  className={styles.dropPrice__form__details__inputs__icon}
                  ariaLabel={tA11y('A11Y.ICON.INFO_SELLER_FEE')}
                />
              </SellerFeesModal>
            </label>
            <div
              className={clsx(
                styles.dropPrice__form__details__inputs__wrapperWithCurrency,
                styles['dropPrice__form__details__inputs__wrapperWithCurrency--output'],
              )}
            >
              <span className={clsx(styles.dropPrice__form__details__inputs__currencySymbol)}>
                {selectedProduct.currencySymbol}
              </span>
              <input
                className={clsx(
                  styles.dropPrice__form__details__inputs__field,
                  styles['dropPrice__form__details__inputs__field--output'],
                )}
                id="sellerEearnings"
                name="sellerEearnings"
                disabled
                value={!errorMessage && !isPriceDetailsLoading && sellerEarning > 0 ? sellerEarning : '...'}
              />
            </div>
          </div>
        </div>
        {errorMessage && (
          <div className={clsx('vc-formUnit', errorMessage && 'error')}>
            <span
              className={clsx(styles.dropPrice__form__details__errorMessage, 'vc-inputText-errorMsg', 'vc-text-xs')}
              data-cy="price-drop-form-error"
            >
              {errorMessage}
            </span>
          </div>
        )}
      </div>
      {isBuyerFeesVisible && (
        <div className={styles.dropPrice__form__buyerFees}>
          {t2('BUYERFEE.DISCLAIMER.SELLER', {
            price: buyerFeesPrice ?? '',
          })}{' '}
          {!errorMessage && (
            <BuyerFeesModal
              isSeller={true}
              trackingContext="pdp"
              productId={selectedProduct.id}
            >
              <div className={styles.dropPrice__form__buyerFees__more}> {t2('BUYERFEE.DISCLAIMER.KNOW_MORE')}</div>
            </BuyerFeesModal>
          )}
        </div>
      )}
      <PriceDropBannerBox />
      <div className={styles.dropPrice__form__submitButton}>
        <Button
          id="price-drop-submit"
          variant="primary"
          size="large"
          disabled={isSubmitButtonDisabled}
          type="submit"
          onClick={onClick}
          fullWidth
          notFullWidthQuery="md-up"
        >
          {t1('PRICE_DROP.BUTTON.DROP_PRICE')}
        </Button>
      </div>
    </form>
  );
};
